import { useEffect, useMemo, useRef, useState } from 'react';

import { SnoozedEmail, UserCommunication } from '@this+that/models';
import log from 'loglevel';

import { useGetSnoozedEmails } from '../../../services/snoozedEmails.service';
import {
  useGetUserCommunicationById,
  useGetUserCommunicationByInternetMessageId,
} from '../../../services/userCommunications.service';
import {
  useIntegrationsStore,
  useUserCommunicationsStore,
} from '../../../store';
import { useInboxAccountFilter } from '../../useInboxAccountFilter';

export interface HydratedSnoozedEmail {
  snoozedEmail: SnoozedEmail;
  userCommunication: UserCommunication | null;
}

const EXPIRY_REFETCH_BUFFER_MS = 5_000;

export const useHydratedSnoozedEmails = () => {
  const {
    snoozedEmails,
    loading: isLoadingList,
    refetch,
  } = useGetSnoozedEmails('cache-and-network');
  const { getUserCommunicationById } = useGetUserCommunicationById();
  const { getUserCommunicationByInternetMessageId } =
    useGetUserCommunicationByInternetMessageId();
  const { getUserCommunicationByMessageId, insertUserCommunication } =
    useUserCommunicationsStore();
  const { integrationAccounts } = useIntegrationsStore();
  const { selectedAccountIds } = useInboxAccountFilter(integrationAccounts);
  const fallbackByIdRef = useRef<Record<string, UserCommunication | null>>({});
  const [isHydrating, setIsHydrating] = useState(false);

  const filteredSnoozedEmails = useMemo(
    () =>
      snoozedEmails.filter((snoozedEmail) =>
        selectedAccountIds.includes(snoozedEmail.userEmailAddress),
      ),
    [snoozedEmails, selectedAccountIds],
  );

  useEffect(() => {
    let cancelled = false;

    const missingSnoozedEmails = filteredSnoozedEmails.filter(
      (snoozedEmail) =>
        !getUserCommunicationByMessageId(snoozedEmail.messageId) &&
        !(snoozedEmail.messageId in fallbackByIdRef.current),
    );

    if (missingSnoozedEmails.length === 0) {
      return;
    }

    setIsHydrating(true);

    const hydrate = async () => {
      const results = await Promise.all(
        missingSnoozedEmails.map(async (snoozedEmail) => {
          const { messageId, internetMessageId } = snoozedEmail;
          try {
            const communication = await getUserCommunicationById(messageId);
            if (communication) {
              return [messageId, communication] as const;
            }
            if (internetMessageId) {
              const fallbackCommunication =
                await getUserCommunicationByInternetMessageId(
                  internetMessageId,
                );
              return [messageId, fallbackCommunication ?? null] as const;
            }
            return [messageId, null] as const;
          } catch (error) {
            log.error('useHydratedSnoozedEmails: failed to hydrate', {
              messageId,
              error,
            });
            return [messageId, null] as const;
          }
        }),
      );

      if (cancelled) {
        return;
      }

      const next = { ...fallbackByIdRef.current };
      results.forEach(([messageId, communication]) => {
        if (communication) {
          insertUserCommunication(communication);
        }
        next[messageId] = communication;
      });
      fallbackByIdRef.current = next;
      setIsHydrating(false);
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [
    filteredSnoozedEmails,
    getUserCommunicationById,
    getUserCommunicationByInternetMessageId,
    getUserCommunicationByMessageId,
    insertUserCommunication,
  ]);

  const items = filteredSnoozedEmails
    .map((snoozedEmail) => ({
      snoozedEmail,
      userCommunication:
        getUserCommunicationByMessageId(snoozedEmail.messageId) ??
        fallbackByIdRef.current[snoozedEmail.messageId] ??
        null,
    }))
    .sort((a, b) => a.snoozedEmail.snoozeUntil - b.snoozedEmail.snoozeUntil);

  useEffect(() => {
    if (filteredSnoozedEmails.length === 0) {
      return;
    }

    const earliestSnoozeUntilMs = Math.min(
      ...filteredSnoozedEmails.map(
        (snoozedEmail) => snoozedEmail.snoozeUntil * 1000,
      ),
    );
    const delayMs = Math.max(
      0,
      earliestSnoozeUntilMs - Date.now() + EXPIRY_REFETCH_BUFFER_MS,
    );

    // ponytail: fixed timeout-to-next-expiry instead of a websocket push —
    // there's no WS event for snooze wake-up today. Upgrade path: a backend
    // WS entity for SnoozedEmail wake, consumed like userCommunicationsSync.ts.
    const timeoutId = setTimeout(() => {
      refetch();
    }, delayMs);

    return () => clearTimeout(timeoutId);
  }, [filteredSnoozedEmails, refetch]);

  return { items, isLoading: isLoadingList || isHydrating, refetch };
};
